<script>
import {mapState, mapActions} from 'vuex';
import moment from 'moment';
import { filter } from 'lodash-es';

export default {
    filters: {
        displayUnits: (units, allocationType) => {
            if (allocationType === 'ALLOTMENT') {
                return units
            } else if (allocationType === 'BLOCKED') {
                return '-';
            } else if (allocationType === 'FREESALE' || allocationType === 'REQUEST') {
                return '∞'
            } else if (allocationType === 'NULL') {
                return ''
            }
            return null;
        }
    },
    props: {
        allocationType: {
            type: String,
            default: 'NULL'
        },
        units: {
            type: Number,
            default: 0
        },
        date: {
            type: Object,
            default: undefined
        },
        tooltip: {
            type: Object,
            default: undefined
        },
        index: {
            type: Number,
            default: undefined
        },
        arrival: {
            type: Boolean,
            default: false
        },
        past: {
            type: Boolean,
            default: false
        },
        current: {
            type: Boolean,
            default: false
        },
        request: {
            type: Object,
            default: undefined
        }
    },
    data() {
        return {
            showTooltip: false
        }
    },
    computed: {
        ...mapState({
            startIndex: state => state.allocation.calendar.startIndex,
            modalOpen: state => state.allocation.modal.open,
            indices: state => state.allocation.request.indices,
        }),
        suffix() {
            if (!this.date) return "";
            let dateElement = moment(this.date).format('D');
            let rootDate = parseInt(dateElement) % 10;
            switch (rootDate) {
            case 1:
                return "st";
            case 2:
                return "nd";
            case 3:
                return "rd";
            default:
                return "th";
            }
        },
    },
    created() {
        window.addEventListener('click', this.deselect);
    },
    methods: {
        ...mapActions({
            selectDate: 'allocation/selectCalendarDate',
            deselectCalendarDate: 'allocation/deselectCalendarDate'
        }),
        deselect($event) {
            // Set root element
            let element = this.getRootElement($event.target);
            if(!this.modalOpen && this.startIndex && !element.classList.contains('manage-allocation-calendar-element')) {
                this.deselectCalendarDate();
                this.resetCalendar();
            }
        },
        select ($event, date) {
            let element = this.getRootElement($event.target);
            // Check if date is in the past
            if (!this.isPast(element)) {
                // Check if the choosen date is the from date or after the or equal the from date
                if (this.isAfterStartDate(element)) {
                    // If pressing the start date the from date and index will be reset in action
                    this.selectDate({day: date.format('DD/MM/YYYY'), index: this.index});
                    // Toggle select
                    this.toggleSelect(element);
                    // If the modal is opened then remove the selected class for all elements
                    if (this.modalOpen) {
                        this.resetCalendar()
                    }
                }
            }
        },
        hover ($event) {
            // Display tooltip
            this.showTooltip = true;
            // If start date is set then select all dates within start and end index
            if (this.startIndex !== undefined) {
                let element = this.getRootElement($event.target);
                // Get all the allocation calendar elements
                let elements = document.getElementsByClassName('manage-allocation-calendar-element');
                this.toggleHover(elements, element.id)
            }
        },
        isPast(element) {
            return element.classList.contains('PAST');
        },
        isAfterStartDate(element) {
            // If startIndex is set then validate index is after.
            if(this.startIndex === undefined) return true;
            return parseInt(element.id) >= parseInt(this.startIndex);
        },
        getRootElement(target) {
            let element = target;
            // Get parent element
            while(element && element.classList.length === 1) {
                element = element.parentNode;
            }
            return element;
        },
        toggleSelect(element) {
            // Toggle the selected class
            if (element.classList.contains('selected')) {
                element.classList.remove('selected');
            } else {
                element.classList.add('selected');
            }
        },
        toggleHover(elements, endIndex) {
            filter(elements, (element) => {
                if (parseInt(element.id) > parseInt(this.startIndex) && parseInt(element.id) < parseInt(endIndex)) {
                    if (!element.classList.contains('selected')) {
                        this.toggleSelect(element);
                    }
                } else {
                    if (parseInt(element.id) !== parseInt(this.startIndex) && element.classList.contains('selected')) {
                        this.toggleSelect(element);
                    }
                }
            })
        },
        resetCalendar() {
            let elements = document.getElementsByClassName('manage-allocation-calendar-element');
            filter(elements, (element) => {
                if (element.classList.contains('selected')) {
                    this.toggleSelect(element);
                }
            })
        },
        hideTooltip() {
            this.showTooltip = false;
        }
    }
}
</script>

<template>
    <div v-if="allocationType"
         :id="index"
         :class="['manage-allocation-calendar-element',
                  (past) ? 'manage-allocation-calendar-element-PAST' : null,
                  'manage-allocation-calendar-element-' + allocationType,
                  (indices.indexOf(index) !== -1) ? 'manage-allocation-calendar-element-edit' : null,
                  (request && request.requested) ? 'manage-allocation-calendar-element-request' : null,
                  (arrival) ? 'manage-allocation-calendar-element-arrival-day' : null,
                  (current) ? 'manage-allocation-calendar-element-current-day' : null,
                  (allocationType === 'ALLOTMENT' && units === 0) ? 'manage-allocation-calendar-element-sold-out' : null]"
         @click="select($event, date)"
         @mouseover="hover"
         @mouseleave="hideTooltip">
        <div v-if="date !== undefined" class="manage-allocation-calendar-element__day">
            <span>{{ date | moment('D') }}</span>
            <span class="manage-allocation-calendar-element__day--postfix">{{ suffix }}</span>
        </div>
        <div v-if="date === undefined" class="manage-allocation-calendar-element__day"/>
        <div class="manage-allocation-calendar-element__units">
            {{ units | displayUnits(allocationType) }}
        </div>
        <!--TODO: Add tooltip from 2019 with allocations/bookings data when they are correct-->
        <div v-if="showTooltip && request && request.requested && !startIndex"
             class="manage-allocation-calendar-element__tooltip">
            <div class="manage-allocation-calendar-element__tooltip-content">
                <!--<div class="">{{ $t('manageAllocationModalCalendarElement.bookings') }}{{ (tooltip.bookings) ? tooltip.bookings : 0 }}</div>-->
                <!--<div class="">{{ $t('manageAllocationModalCalendarElement.units') }}{{ (tooltip.initialUnits) !== null ? tooltip.initialUnits : '∞' }}</div>-->
                <div class="">{{ $t('manageAllocationModalCalendarElement.request.header').toUpperCase() }}</div>
                <div v-if="request.units" class="">
                    <span>{{ $t('manageAllocationModalCalendarElement.request.deduct') }} </span>
                    <span>{{ request.units }} </span>
                    <span>{{ $t('manageAllocationModalCalendarElement.request.units') }}</span>
                </div>
                <div v-if="request.releaseDate || request.releaseDays" class="">
                    <span>
                        {{ $t('manageAllocationModalCalendarElement.request.release') }}
                    </span>
                    <span v-if="request.releaseDate">
                        {{ request.releaseDate | moment("DD/MM/YYYY") }}
                    </span>
                    <span v-if="request.releaseDays">
                        {{ request.releaseDays }} {{ $t('manageAllocationModalCalendarElement.request.days') }}
                    </span>
                </div>
                <div v-if="request.type" class="">{{ $t('manageAllocationModalCalendarElement.request.type') }} {{ request.type }}</div>
            </div>
        </div>
    </div>
</template>



// WEBPACK FOOTER //
// src/components/ManageAllocationCalendarElement/ManageAllocationCalendarElement.vue